import { Fragment } from 'react';
import { ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { CapturedMediaThumb } from './captured-media';
import { DECISION_LABELS, DECISION_PILL_CLASSES } from '../action-log-constants';
import { commentTextOrMediaAlone, formatLogTimestamp, orNotRecorded } from '../action-log-format';
import type { SocialActionDecision, SocialActionLogRow } from '../types';
import { PILL_BUTTON } from '../ui-chrome';

/**
 * The log itself — Figma 4768:14486.
 *
 * One row per comment, and a row opens a panel rather than expanding in place. The old table
 * expanded downward, which meant the row you were reading pushed every row below it off screen;
 * the panel keeps the list still while you read one decision.
 *
 * There is no delete and no edit. This is the record of what the agent did to a customer in public.
 */

/**
 * The design's column widths, shared by the header and the rows so neither hard-codes offsets the
 * other has to match. Comment is the only flexible one — it is the only column whose content has
 * no natural width.
 */
const GRID = 'grid grid-cols-[120px_minmax(0,1fr)_240px_240px_240px_16px] items-center';

/**
 * The rule that opens every column but the first — 24px and CENTRED, not the row's full height.
 *
 * It is the height of the cell's content, so it separates two values rather than drawing a cell
 * wall. Run full height it reaches the horizontal dividers at both ends and the table stops reading
 * as a list of rows and starts reading as a grid of boxes, which is what made the spacing look off.
 */
const COLUMN_RULE =
	'before:absolute before:left-0 before:top-1/2 before:h-6 before:w-px before:-translate-y-1/2 before:bg-gray-300 before:content-[""]';
const CELL = `relative flex h-10 items-center pl-2.5 pr-3 ${COLUMN_RULE}`;
const FIRST_CELL = 'flex h-10 items-center pr-3';

export const DecisionPill = ({ decision }: { decision: SocialActionDecision }) => (
	<span
		className={cn(
			'inline-flex w-fit items-center rounded-full px-2 py-0.5 text-xs font-medium',
			DECISION_PILL_CLASSES[decision]
		)}
	>
		{DECISION_LABELS[decision]}
	</span>
);

interface ActionLogTableProps {
	rows: SocialActionLogRow[];
	hasMore: boolean;
	isLoadingMore: boolean;
	onLoadMore: () => void;
	/** Which row's panel is open, so the row can show itself as the one being read. */
	selectedId: string | null;
	onSelect: (row: SocialActionLogRow) => void;
}

const COLUMNS = ['Ticket ID', 'Comment', 'Rule triggered', 'Decision', 'Time Stamp'];

const HeaderRow = () => (
	<div className={`${GRID} h-10 border-y border-gray-300 bg-gray-100 px-4 text-xs text-gray-800`}>
		{COLUMNS.map((column, index) => (
			<div key={column} className={index === 0 ? FIRST_CELL : CELL}>
				{column}
			</div>
		))}
		<span />
	</div>
);

/**
 * The line between two rows — Figma's Horizontal/Middle-inset.
 *
 * INSET by 16px, not full-bleed: it separates two rows inside one list, and a line running edge to
 * edge reads as the end of the table instead. The only full-width rules on this page are the two
 * around the header, which is exactly the boundary they mark.
 */
const RowDivider = () => (
	<div className='px-4' aria-hidden='true'>
		<div className='h-px w-full bg-gray-300' />
	</div>
);

/**
 * The comment, as much of it as a row can hold.
 *
 * The thumbnail goes FIRST: a media-only comment has no text, so without it the row reads as empty
 * and looks like a bug rather than a picture. And when the picture is the whole comment nothing is
 * written beside it — a stand-in like "attachment only" only repeats what is already visible.
 */
const CommentCell = ({ row }: { row: SocialActionLogRow }) => {
	const text = commentTextOrMediaAlone(row.commentSnapshot, row.mediaSnapshotRefs?.length ?? 0);

	return (
		<div className={CELL}>
			<CapturedMediaThumb refs={row.mediaSnapshotRefs} />
			{text !== null && <span className='truncate'>{text}</span>}
		</div>
	);
};

const LogRow = ({
	row,
	isSelected,
	onSelect
}: {
	row: SocialActionLogRow;
	isSelected: boolean;
	onSelect: () => void;
}) => (
	// 44 outside, 40 inside. The tint is on the inner block, so a hovered or open row stops 2px
	// short of the dividers above and below rather than running into them — the rows read as a list
	// rather than as one continuously ruled block.
	<div
		role='button'
		tabIndex={0}
		aria-current={isSelected}
		onClick={onSelect}
		onKeyDown={(event) => {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				onSelect();
			}
		}}
		className='flex h-11 shrink-0 cursor-pointer flex-col justify-center'
	>
		<div
			className={cn(
				`${GRID} h-10 rounded-md px-[18px] text-[13px] text-ink hover:bg-gray-50`,
				isSelected && 'bg-gray-100'
			)}
		>
			<div className={FIRST_CELL}>
				<span className='truncate'>{orNotRecorded(row.ticketDisplayId)}</span>
			</div>

			{/* Truncated here and shown in full in the panel — the column is a way of recognising the
			    row, not of reading the comment. */}
			<CommentCell row={row} />

			<div className={CELL}>
				<span className='truncate'>{orNotRecorded(row.ruleName)}</span>
			</div>

			<div className={CELL}>
				<DecisionPill decision={row.decision} />
			</div>

			<div className={CELL}>
				<span className='truncate'>{formatLogTimestamp(row.createdAt)}</span>
			</div>

			<ChevronRight className='size-4 text-gray-800' aria-hidden='true' />
		</div>
	</div>
);

/**
 * Loaded against total.
 *
 * "Loaded" rather than a page number because pagination is keyset — there is no total to count
 * towards until the last page arrives, at which point the loaded count IS the total. Inventing a
 * total would mean a second full scan of an append-only table for a number stale on arrival.
 *
 * The zone used to be named here as a standing note under every page. It sat permanently in view
 * to answer a question asked once, in one place — when someone quotes a time at Meta or at the
 * helpdesk — so it moved onto the timestamp in the detail panel, which is where that time gets
 * copied from.
 */
const Footer = ({
	loaded,
	hasMore,
	isLoadingMore,
	onLoadMore
}: {
	loaded: number;
	hasMore: boolean;
	isLoadingMore: boolean;
	onLoadMore: () => void;
}) => (
	<div className='flex flex-wrap items-center justify-between gap-3 px-4 py-4'>
		<p className='text-xs text-muted-foreground'>
			{hasMore ? `${loaded} rows loaded · more to load` : `Showing all ${loaded} rows`}
		</p>

		{hasMore && (
			<Button
				variant='outline'
				className={PILL_BUTTON}
				disabled={isLoadingMore}
				onClick={onLoadMore}
			>
				{isLoadingMore && <Loader2 className='size-3.5 animate-spin' />} Load more
			</Button>
		)}
	</div>
);

export const ActionLogTable = ({
	rows,
	hasMore,
	isLoadingMore,
	onLoadMore,
	selectedId,
	onSelect
}: ActionLogTableProps) => (
	<div className='flex flex-col'>
		<HeaderRow />

		{/* 2px between a row and the divider under it, per the design's list stack. */}
		<div className='flex flex-col gap-0.5'>
			{rows.map((row, index) => (
				<Fragment key={row.id}>
					{index > 0 && <RowDivider />}
					<LogRow row={row} isSelected={row.id === selectedId} onSelect={() => onSelect(row)} />
				</Fragment>
			))}
		</div>

		<Footer loaded={rows.length} hasMore={hasMore} isLoadingMore={isLoadingMore} onLoadMore={onLoadMore} />
	</div>
);
